import { Link } from "react-router-dom";
import PitBoardLogo from "@/components/PitBoardLogo";
import {
  CheckCircle2, Calculator, FileText, TrendingUp, Boxes, Users, Wrench,
  ShieldCheck, Sparkles, ArrowRight, Smartphone, Lock,
} from "lucide-react";

const FEATURES = [
  { icon: Calculator, title: "Coût horaire calculé", desc: "Taux de facturation minimum auto-calculé avec vos charges fixes, salaires et marge cible." },
  { icon: FileText, title: "Devis & factures PDF", desc: "Modèles propres prêts à envoyer, avec CGV, RIB, TVA franchise et mentions auto-entrepreneur." },
  { icon: TrendingUp, title: "Trésorerie en direct", desc: "Cockpit financier, projections de marge, IS prévisionnel et alerte de break-even." },
  { icon: Boxes, title: "Stock & matériaux", desc: "Bibliothèque d'ouvrages, articles achat-revente, décrément auto à la facturation." },
  { icon: Users, title: "CRM clients", desc: "Tous vos clients, devis, factures et fiches chantier dans une vue unique." },
  { icon: Wrench, title: "Fiches chantier", desc: "Listes de matériel à commander, à amener, et bon de travail signé par le client." },
];

const PRICING_INCLUDES = [
  "Devis & factures illimités",
  "Bibliothèque de prix d'ouvrages",
  "Stock & inventaire",
  "Trésorerie & break-even",
  "PDFs personnalisés à votre logo",
  "Mode auto-entrepreneur / TNS",
  "Multi-chantiers",
  "Support email sous 48h",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-app text-[var(--chalk)]" data-testid="landing-page">
      {/* Top nav */}
      <nav className="border-b border-[var(--steel)] sticky top-0 z-40 backdrop-blur bg-[rgba(11,11,12,0.78)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <PitBoardLogo size={48} />
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="#features" className="hidden sm:inline text-sm text-[var(--ink-soft)] hover:text-[var(--chalk)]">Fonctionnalités</a>
            <a href="#pricing" className="hidden sm:inline text-sm text-[var(--ink-soft)] hover:text-[var(--chalk)]">Tarif</a>
            <Link to="/packs-vitrine" className="hidden sm:inline text-sm text-[var(--ink-soft)] hover:text-[var(--chalk)]" data-testid="nav-packs-vitrine">Packs Métiers</Link>
            <a href="#faq" className="hidden sm:inline text-sm text-[var(--ink-soft)] hover:text-[var(--chalk)]">FAQ</a>
            <Link to="/login" className="btn btn-outline btn-sm" data-testid="nav-login">Connexion</Link>
            <Link to="/login" className="btn btn-primary btn-sm" data-testid="nav-cta">Essai gratuit</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative carbon-texture overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.55) 0%, rgba(11,11,12,0.92) 100%)" }} />
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "var(--pit-yellow)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="flex items-center gap-2 font-body text-xs font-semibold mb-6" style={{ color: "var(--pit-yellow)" }}>
                <span className="ornament" style={{ background: "var(--pit-yellow)" }} />
                LOGICIEL DE GESTION POUR ENTREPRENEURS &amp; INDÉPENDANTS
              </p>
              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-7">
                VOTRE BUSINESS<br />
                À <span style={{ color: "var(--pit-yellow)" }}>360°.</span><br />
                ZÉRO <span style={{ color: "var(--board-red)" }}>ANGLE MORT.</span>
              </h1>
              <p className="text-lg max-w-2xl mb-9 leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                Devis, factures, coût horaire, trésorerie, stock, fiches chantier.
                Toute votre gestion dans un seul cockpit, pensé pour le terrain et conforme à la réglementation française.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Link to="/login" className="btn btn-primary" style={{ minHeight: 54, fontSize: "0.95rem" }} data-testid="hero-cta">
                  Commencer l'essai gratuit 14 jours <ArrowRight size={16} />
                </Link>
                <a href="#pricing" className="btn btn-outline" style={{ minHeight: 54 }}>
                  Voir le tarif
                </a>
              </div>
              <div className="flex items-center gap-5 text-sm" style={{ color: "var(--grey)" }}>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} style={{ color: "var(--pit-yellow)" }} /> Sans carte bancaire</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} style={{ color: "var(--pit-yellow)" }} /> Annulation 1 clic</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} style={{ color: "var(--pit-yellow)" }} /> RGPD</span>
              </div>
            </div>

            {/* Floating preview card */}
            <div className="lg:col-span-5">
              <div className="rounded-xl border p-5 shadow-2xl" style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}>
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--steel)]">
                  <div className="text-xs font-semibold" style={{ color: "var(--pit-yellow)" }}>COCKPIT MENSUEL · MARS 2026</div>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(255,196,0,0.12)", color: "var(--pit-yellow)" }}>LIVE</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <KpiCard label="CA HT" value="24 850 €" />
                  <KpiCard label="Résultat net" value="+ 6 420 €" accent />
                  <KpiCard label="Trésorerie" value="18 230 €" />
                  <KpiCard label="Marge brute" value="38 %" />
                </div>
                <div className="space-y-2">
                  <PreviewRow label="Devis Maison Dupont — Salon" value="3 420 €" status="signé" />
                  <PreviewRow label="Facture Cabinet Bertrand" value="1 280 €" status="payée" />
                  <PreviewRow label="Stock peinture mate" value="42 L" status="ok" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <p className="font-body text-xs font-semibold mb-3" style={{ color: "var(--board-red)" }}>FONCTIONNALITÉS</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-5">
              Tout ce dont vous avez besoin.
              <br /><span style={{ color: "var(--pit-yellow)" }}>Rien de superflu.</span>
            </h2>
            <p className="text-lg" style={{ color: "var(--ink-soft)" }}>
              Conçu avec des entrepreneurs, prestataires de services et indépendants français : peintres, plombiers, électriciens, paysagistes, consultants. Plus de tableurs, plus d'oublis de factures, plus de devis perdus.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-xl border p-6 hover:border-[var(--pit-yellow)] transition-colors" style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 border" style={{ background: "var(--carbon)", borderColor: "var(--steel)" }}>
                  <Icon size={18} style={{ color: "var(--pit-yellow)" }} strokeWidth={2} />
                </div>
                <div className="font-display text-lg mb-1.5" style={{ fontWeight: 700 }}>{title}</div>
                <div className="text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 border-t border-[var(--steel)]" style={{ background: "var(--carbon-2)" }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-body text-xs font-semibold mb-3" style={{ color: "var(--pit-yellow)" }}>TARIFICATION</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-4">
              Un tarif unique. <br className="sm:hidden" /><span style={{ color: "var(--board-red)" }}>Tout inclus.</span>
            </h2>
            <p className="text-lg" style={{ color: "var(--ink-soft)" }}>
              Pas de palier, pas de surprise. Tout PitBoard pour 19 € HT/mois — le plus complet et le moins cher de France.
            </p>
          </div>

          <div className="rounded-2xl border-2 p-8 sm:p-10 max-w-2xl mx-auto" style={{ background: "var(--carbon)", borderColor: "var(--pit-yellow)" }}>
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <div>
                <div className="text-xs font-semibold mb-1" style={{ color: "var(--pit-yellow)" }}>PITBOARD PRO</div>
                <h3 className="font-display text-3xl" style={{ fontWeight: 700 }}>Tout, tout de suite.</h3>
              </div>
              <span className="text-[11px] px-3 py-1 rounded-full" style={{ background: "rgba(255,196,0,0.12)", color: "var(--pit-yellow)" }}>
                ESSAI 14 JOURS OFFERT
              </span>
            </div>

            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display text-6xl numerals" style={{ fontWeight: 700 }}>19 €</span>
              <span className="text-lg" style={{ color: "var(--ink-soft)" }}>HT / mois</span>
            </div>
            <p className="text-sm mb-7" style={{ color: "var(--grey)" }}>
              <span style={{ color: "var(--ink-soft)" }}>Soit 22,80 € TTC/mois</span> (TVA 20 %). Sans engagement, annulation en 1 clic.
            </p>

            <Link to="/login" className="btn btn-primary w-full mb-6" style={{ minHeight: 56, fontSize: "1rem" }} data-testid="pricing-cta">
              Démarrer l'essai gratuit <ArrowRight size={16} />
            </Link>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {PRICING_INCLUDES.map((it) => (
                <div key={it} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: "var(--pit-yellow)" }} />
                  <span style={{ color: "var(--chalk)" }}>{it}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            <Reassurance icon={Lock} title="Données sécurisées" desc="Hébergement européen, chiffrement TLS, sauvegardes quotidiennes." />
            <Reassurance icon={Smartphone} title="Mobile-first" desc="Pilotez depuis le chantier sur smartphone, tablette ou bureau." />
            <Reassurance icon={ShieldCheck} title="RGPD & France" desc="Conforme RGPD. CGU et facturation TVA française." />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-10 text-center">Questions fréquentes</h2>
          <div className="space-y-3">
            <Faq q="Comment fonctionne l'essai gratuit ?" a="14 jours d'accès complet à toutes les fonctionnalités, sans carte bancaire. À la fin, vous choisissez de vous abonner pour 19 € HT/mois (22,80 € TTC) ou de stopper, sans frais." />
            <Faq q="Puis-je annuler à tout moment ?" a="Oui, en 1 clic depuis votre espace abonnement. Aucune pénalité, aucun engagement. Vos données restent accessibles 30 jours après annulation puis sont supprimées sur demande (RGPD)." />
            <Faq q="Où sont stockées mes données ?" a="Vos données sont hébergées en Europe sur des serveurs sécurisés (chiffrement TLS, sauvegardes quotidiennes). Aucune donnée carte bancaire n'est conservée chez nous : tout est géré par Stripe (PCI-DSS niveau 1)." />
            <Faq q="Mes clients ont-ils besoin d'un compte ?" a="Non. Vous envoyez vos devis et factures par email ou PDF à vos clients. Ils n'ont rien à installer." />
            <Faq q="Suis-je auto-entrepreneur compatible ?" a="Oui. PitBoard gère le mode auto-entrepreneur (franchise TVA, mentions légales obligatoires sur PDF, masquage du capital) et le mode société (SARL, SAS, EURL, etc.)." />
            <Faq q="Y a-t-il une formation ?" a="L'interface est conçue pour être prise en main en 15 minutes. Support email sous 48h ouvrées." />
          </div>
        </div>
      </section>

      {/* CTA bottom */}
      <section className="py-20 carbon-texture relative">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.85) 0%, rgba(11,11,12,0.95) 100%)" }} />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Sparkles size={28} style={{ color: "var(--pit-yellow)" }} className="mx-auto mb-4" />
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
            Reprenez la <span style={{ color: "var(--pit-yellow)" }}>main</span> sur votre gestion.
          </h2>
          <p className="text-lg mb-8" style={{ color: "var(--ink-soft)" }}>
            Rejoignez les entrepreneurs qui pilotent leur business en temps réel.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ minHeight: 56, fontSize: "1rem" }} data-testid="bottom-cta">
            Démarrer mon essai gratuit <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--steel)] py-10" style={{ background: "var(--carbon)" }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <PitBoardLogo size={42} />
            <p className="text-xs mt-3" style={{ color: "var(--grey)" }}>
              Édité par MJSA INVEST · © 2026 PitBoard · Tous droits réservés
            </p>
          </div>
          <div className="flex flex-wrap gap-5 text-sm">
            <Link to="/cgu" className="text-[var(--ink-soft)] hover:text-[var(--chalk)]" data-testid="footer-cgu">CGU</Link>
            <Link to="/packs-vitrine" className="text-[var(--ink-soft)] hover:text-[var(--chalk)]" data-testid="footer-packs">Packs Métiers</Link>
            <Link to="/confidentialite" className="text-[var(--ink-soft)] hover:text-[var(--chalk)]" data-testid="footer-privacy">Confidentialité</Link>
            <Link to="/mentions-legales" className="text-[var(--ink-soft)] hover:text-[var(--chalk)]" data-testid="footer-legal">Mentions légales</Link>
            <a href="mailto:contact@pitboard-app.fr" className="text-[var(--ink-soft)] hover:text-[var(--chalk)]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className="rounded-md p-3" style={{ background: "var(--carbon)" }}>
      <div className="text-[10px] mb-1" style={{ color: "var(--grey)" }}>{label}</div>
      <div className="font-display text-xl numerals" style={{ fontWeight: 700, color: accent ? "var(--pit-yellow)" : "var(--chalk)" }}>{value}</div>
    </div>
  );
}

function PreviewRow({ label, value, status }) {
  const dot = { signé: "var(--pit-yellow)", payée: "#22c55e", ok: "var(--ink-soft)" }[status] || "var(--ink-soft)";
  return (
    <div className="flex items-center justify-between text-[13px] py-1.5 border-b border-[var(--steel)] last:border-0">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
        <span style={{ color: "var(--chalk)" }}>{label}</span>
      </div>
      <span className="numerals font-semibold">{value}</span>
    </div>
  );
}

function Reassurance({ icon: Icon, title, desc }) {
  return (
    <div className="text-center px-4 py-5">
      <Icon size={22} className="mx-auto mb-3" style={{ color: "var(--pit-yellow)" }} strokeWidth={2} />
      <div className="font-semibold text-sm mb-1.5">{title}</div>
      <div className="text-xs leading-relaxed" style={{ color: "var(--ink-soft)" }}>{desc}</div>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="rounded-lg border p-5 group" style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}>
      <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
        <span className="font-semibold">{q}</span>
        <span className="text-xl shrink-0 transition-transform group-open:rotate-45" style={{ color: "var(--pit-yellow)" }}>+</span>
      </summary>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>{a}</p>
    </details>
  );
}
